<template>
  <div class="text-lines-input">
    <label v-if="label" class="text-lines-input__label">{{ label }}</label>
    <div class="text-lines-input__group">
      <template v-if="useKeyedFields">
        <v-text-field
          v-for="field in fields"
          :key="field.key"
          :value="keyedValues[field.key]"
          outlined
          dense
          hide-details
          :placeholder="field.label || field.key"
          class="text-lines-input__field"
          @input="onKeyedInput(field.key, $event)"
        />
      </template>
      <template v-else>
        <v-text-field
          v-for="(_, i) in lineCount"
          :key="i"
          :value="lines[i]"
          outlined
          dense
          hide-details
          :placeholder="placeholders[i] || `Line ${i + 1}`"
          class="text-lines-input__field"
          @input="onLineInput(i, $event)"
        />
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TextLinesInput',
  props: {
    label: { type: String, default: '' },
    /** Array mode: string[]. Keyed mode (with `fields`): Record<key, string>. */
    value: { type: [Array, Object], default: () => [] },
    /** Keyed mode: [{ key, label }] — value is an object keyed by `key`. */
    fields: { type: Array, default: null },
    /** Number of text fields in array mode (default 4). */
    lineCount: { type: Number, default: 4 },
    /** Placeholders for each line in array mode, e.g. ['Line 1', 'Line 2', ...] */
    placeholders: { type: Array, default: () => [] },
  },
  computed: {
    useKeyedFields() {
      return Array.isArray(this.fields) && this.fields.length > 0
    },
    keyedValues() {
      const v = this.value && typeof this.value === 'object' && !Array.isArray(this.value) ? this.value : {}
      const out = {}
      for (const field of this.fields || []) {
        out[field.key] = v[field.key] ?? ''
      }
      return out
    },
    lines() {
      const v = this.value || []
      const arr = Array.from({ length: this.lineCount }, (_, i) => v[i] ?? '')
      return arr
    },
  },
  methods: {
    onKeyedInput(key, val) {
      this.$emit('input', {
        ...this.keyedValues,
        [key]: val == null ? '' : String(val),
      })
    },
    onLineInput(index, val) {
      const arr = [...this.lines]
      arr[index] = val == null ? '' : String(val)
      this.$emit('input', arr)
    },
  },
}
</script>

<style scoped>
.text-lines-input__label {
  display: block;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 6px;
  cursor: default;
}

.text-lines-input__group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.text-lines-input__field >>> .v-input__slot fieldset {
  border-radius: 8px;
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.15);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.text-lines-input__field >>> .v-input__slot fieldset:hover {
  border-color: rgba(255, 255, 255, 0.14);
}

.text-lines-input__field >>> .v-input__slot input {
  font-size: 0.875rem;
}

.text-lines-input__field.v-input--is-focused >>> .v-input__slot fieldset {
  border-color: rgba(26, 115, 232, 0.6) !important;
  box-shadow: 0 0 0 1px rgba(26, 115, 232, 0.2);
}
</style>
